import {Injectable} from "@angular/core";
import {JSONMapped, jsonProperty} from "json-mapper-reflect";
import {MasterAircraftWakeupSettings} from "../master-aircraft-wakeup-settings";

/**
 * Service utils to perform operation on preferred update date/time.
 */
@Injectable()
export class DateUtilService {

	/**
	 * @param wakeupSettings AircraftWakeupSettings object
	 * get the last known preferred time settings for this aircraft and reset all relevant fields
	 * in the event a set fails.
	 *
	 * wakeupTime comes from the 2.0 server as an integer representing the number of seconds after midnight UTC.
	 * Ex. 0 would be midnight UTC. 81000 corresponds to 22:30 UTC. (You can use the math below for the conversion)
	 *
	 * @return PreferredDate object {hours, minutes}
	 */
	getLastKnownSettings(wakeupSettings: MasterAircraftWakeupSettings): PreferredDate {
		if (wakeupSettings) {
			let retrievedMinutes = wakeupSettings.wakeupTime / 60;
			let previousUserHoursZulu = Math.floor(retrievedMinutes / 60);
			let previousUserMinutesZulu = Math.ceil((retrievedMinutes % 60) / 15) * 15;
			return this.convertZuluToLocal(previousUserHoursZulu, previousUserMinutesZulu);
		}
		return this.convertZuluToLocal(0, 0);
	}

	/**
	 * Converts Zulu time (UTC) to local time
	 * @param zuluHours UTC Hours
	 * @param zuluMinutes UTC Minutes
	 * @return PreferredDate object {hours, minutes}
	 */
	convertZuluToLocal(zuluHours: number, zuluMinutes: number): PreferredDate {
		let date = new Date();
		let preferredDate = new PreferredDate();
		date.setUTCHours(zuluHours);
		date.setUTCMinutes(zuluMinutes);
		preferredDate.hour = +this.formatTime(date.getHours().toString());
		preferredDate.minutes = +this.formatTime(date.getMinutes().toString());
		return preferredDate;
	}

	/**
	 * Formats the time for display
	 * @param time the time to be formatted
	 */
	formatTime(time: string): string {
		if(time.length == 1) {
			return "0" + time;
		}
		return time;
	}

	/**
	 * Gets preferred-update time and convert it into Zulu time (UTC)
	 * @param wakeupSettings AircraftWakeupSettings object
	 * @return PreferredDate object {hours, minutes}
	 */
	getLastKnownZuluSettings(wakeupSettings: MasterAircraftWakeupSettings) : PreferredDate {
		let preferredDate = new PreferredDate();
		if(wakeupSettings) {
			let retrievedMinutes = wakeupSettings.wakeupTime / 60;
			preferredDate.hour = Math.floor(retrievedMinutes / 60);
			preferredDate.minutes = Math.ceil((retrievedMinutes % 60) / 15) * 15;
		} else {
			preferredDate = this.getPreferredTimeDefault();
		}
		return preferredDate;
	}

	getPreferredTimeDefault() : PreferredDate {
		let preferredDate = new PreferredDate();
		preferredDate.hour = +this.formatTime('0');
		preferredDate.minutes = +this.formatTime('0');
		return preferredDate;
	}
}

export class PreferredDate extends JSONMapped() {

	@jsonProperty()
	hour: number;

	@jsonProperty()
	minutes: number;

	constructor(data?: Partial<PreferredDate>) {
		super();
		Object.assign(this, data);
	}
}
